'use client'

import { FilterDropdown } from '@/components/Dashboard/FilterDropdown'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { useFilterState } from '@/hooks/useFilterState'
import { applyFiltersAndSort, SortMode } from '@/lib/utils/filterUtils'
import { useVirtualizer } from '@tanstack/react-virtual'
import { motion } from 'framer-motion'
import { Bookmark, Heart } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AppSearchToolbar from '../Dashboard/AppSearchToolbar'
import NoResultsFound from '../Dashboard/NoResultsFound'

interface Tag {
  id: string
  name: string
  workspaceId?: string | null
}

interface ListItem {
  id: string
  updatedAt: string
  createdAt: string
  isBookmarked?: boolean
  tags?: Tag[]
}

type FilterVariant = 'bookmark' | 'favorite'

interface ListPageTemplateProps<T extends ListItem> {
  title: string
  items: T[]
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  pagination?: {
    total: number
    hasMore: boolean
  }
  onLoadMore: () => void
  availableTags?: Tag[]
  renderItem: (item: T, index: number) => React.ReactNode
  getItemName: (item: T) => string
  getItemDescription?: (item: T) => string
  emptyStateMessage?: string
  emptyStateSubMessage?: string
  errorMessage?: string
  searchPlaceholder?: string
  _itemTypeSingular?: string
  itemTypePlural?: string
  maxColumnCount?: number
  filterVariant?: FilterVariant
}

export function ListPageTemplate<T extends ListItem>({
  title,
  items,
  isLoading,
  isError,
  isFetching,
  pagination,
  onLoadMore,
  availableTags = [],
  renderItem,
  getItemName,
  emptyStateMessage,
  emptyStateSubMessage,
  errorMessage,
  _itemTypeSingular = 'item',
  itemTypePlural = 'items',
  maxColumnCount = 3,
  filterVariant = 'bookmark',
}: ListPageTemplateProps<T>) {
  const hasAnimatedRef = useRef(false)
  const lastScrollTopRef = useRef(0)
  const scrollDirectionRef = useRef<'down' | 'up'>('down')
  const parentRef = useRef<HTMLDivElement>(null)
  const measuredHeight = useRef(176)
  const measureRef = useRef<HTMLDivElement>(null)

  // Use URL-backed filter state
  const [filterState, filterActions] = useFilterState()

  const availableSortModes = useMemo<SortMode[]>(
    () => ['Last Edited', 'Alphabetical', 'Created'],
    []
  )

  // Apply filters and sorting using composable utility
  const filteredAndSortedItems = useMemo(() => {
    return applyFiltersAndSort(
      items,
      {
        search: {
          value: filterState.search,
          getName: getItemName,
        },
        bookmark: filterState.bookmarkedOnly,
        tags: filterState.tagIds,
      },
      {
        mode: filterState.sortMode,
        order: filterState.sortOrder,
        getName: getItemName,
      }
    )
  }, [items, filterState, getItemName])

  // Calculate grid columns based on available width and minimum card width
  const MIN_CARD_WIDTH = 600
  const GAP = 8 // gap-2 = 8px

  const getColumnCount = () => {
    if (typeof window === 'undefined') return maxColumnCount
    if (!parentRef.current) return maxColumnCount
    const containerWidth = parentRef.current.clientWidth
    const columns = Math.floor((containerWidth + GAP) / (MIN_CARD_WIDTH + GAP))
    return Math.max(1, Math.min(maxColumnCount, columns))
  }

  const [columnCount, setColumnCount] = useState(maxColumnCount)

  // Find the actual scrolling parent element
  const getScrollParent = useCallback(() => {
    let element = parentRef.current?.parentElement
    while (element) {
      const { overflow, overflowY } = window.getComputedStyle(element)
      if (
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowY === 'auto' ||
        overflowY === 'scroll'
      ) {
        return element
      }
      element = element.parentElement
    }
    return null
  }, [])

  // Create virtualizer for grid
  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(filteredAndSortedItems.length / columnCount),
    getScrollElement: getScrollParent,
    estimateSize: () => measuredHeight.current,
    overscan: 2,
  })

  useEffect(() => {
    const handleResize = () => {
      const newColumnCount = getColumnCount()
      setColumnCount((current) => (newColumnCount !== current ? newColumnCount : current))
    }

    handleResize()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [maxColumnCount])

  // Measure card height from first rendered item
  useEffect(() => {
    if (!measureRef.current) {
      return
    }

    const measureHeight = () => {
      if (!measureRef.current) {
        return
      }
      const height = measureRef.current.getBoundingClientRect().height
      if (height > 0) {
        const nextHeight = height + 16
        if (nextHeight !== measuredHeight.current) {
          measuredHeight.current = nextHeight
          // Force the virtualizer to recompute row measurements when the
          // estimated item height changes to avoid visual gaps.
          rowVirtualizer.measure()
        }
      }
    }

    // Measure immediately
    measureHeight()

    // Also observe for dynamic changes
    const observer = new ResizeObserver(() => {
      measureHeight()
    })
    observer.observe(measureRef.current)

    // Fallback measurement after animations settle
    const timer = setTimeout(measureHeight, 400)

    return () => {
      observer.disconnect()
      clearTimeout(timer)
    }
  }, [filteredAndSortedItems.length, rowVirtualizer])

  // Create virtualizer for grid
  // Remeasure virtualizer when height changes
  useEffect(() => {
    if (measuredHeight.current !== 176) {
      rowVirtualizer.measure()
    }
  }, [measuredHeight.current, rowVirtualizer])

  // Track if we've already triggered a load to prevent duplicate requests
  const loadingTriggeredRef = useRef(false)

  // Load more on scroll
  useEffect(() => {
    const scrollElement = getScrollParent()
    if (!scrollElement) return

    const handleScroll = () => {
      if (!pagination?.hasMore || isFetching || loadingTriggeredRef.current) {
        return
      }

      const { scrollTop, scrollHeight, clientHeight } = scrollElement
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

      if (scrollPercentage > 0.8) {
        loadingTriggeredRef.current = true
        onLoadMore()
      }
    }

    scrollElement.addEventListener('scroll', handleScroll, { passive: true })
    return () => scrollElement.removeEventListener('scroll', handleScroll)
  }, [getScrollParent, pagination?.hasMore, isFetching, onLoadMore])

  // Reset loading trigger when fetch completes
  useEffect(() => {
    if (!isFetching) {
      loadingTriggeredRef.current = false
    }
  }, [isFetching])

  // Mark initial animation as complete after first data loads
  useEffect(() => {
    if (filteredAndSortedItems.length > 0 && !hasAnimatedRef.current) {
      const timer = setTimeout(() => {
        hasAnimatedRef.current = true
      }, 1000) // Allow time for stagger animation
      return () => clearTimeout(timer)
    }
  }, [filteredAndSortedItems.length])

  // Track scroll direction
  useEffect(() => {
    const scrollElement = getScrollParent()
    if (!scrollElement) return

    const handleScrollDirection = () => {
      const currentScrollTop = scrollElement.scrollTop
      if (currentScrollTop > lastScrollTopRef.current) {
        scrollDirectionRef.current = 'down'
      } else if (currentScrollTop < lastScrollTopRef.current) {
        scrollDirectionRef.current = 'up'
      }
      lastScrollTopRef.current = currentScrollTop
    }

    scrollElement.addEventListener('scroll', handleScrollDirection, { passive: true })
    return () => scrollElement.removeEventListener('scroll', handleScrollDirection)
  }, [getScrollParent])

  const hasActiveFilters =
    filterState.search !== '' || filterState.tagIds.length > 0 || filterState.bookmarkedOnly

  if (isError) {
    return (
      <div className="pt-12">
        <div className="container mx-auto flex flex-col gap-y-4 px-3 pb-20 md:px-10">
          <p className="text-red-500">
            {errorMessage || `Failed to load ${itemTypePlural}. Please try again later.`}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div ref={parentRef} className="h-full w-full">
      <div className="container mx-auto flex flex-col px-3 pb-20 md:px-10">
        <div className="mb-2 mt-2 flex w-full flex-col items-start gap-2 md:flex-row md:items-center md:justify-between">
          <p
            className="text-left text-xl text-gray-900 md:text-2xl"
            style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.01em' }}
          >
            {title}
          </p>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-start sm:justify-start">
            <FilterDropdown
              showBookmarkedOnly={filterState.bookmarkedOnly}
              selectedTagIds={filterState.tagIds}
              availableTags={availableTags}
              onBookmarkToggle={filterActions.toggleBookmark}
              onTagToggle={filterActions.toggleTag}
              onClearFilters={filterActions.clearFilters}
              variant={filterVariant}
            />
            <div className="-m-4">
              <AppSearchToolbar
                categories={[]}
                searchValue={filterState.search}
                onSearchChange={filterActions.setSearch}
                availableSortModes={availableSortModes}
                activeSortMode={filterState.sortMode}
                activeSortOrder={filterState.sortOrder}
                onSortModeChange={filterActions.setSortMode}
                onSortOrderChange={filterActions.setSortOrder}
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <ul
            className="grid gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))' }}
          >
            {Array.from({ length: 9 }).map((_, idx) => (
              <motion.li
                key={`skeleton-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.3,
                  delay: idx * 0.05,
                  ease: 'easeOut',
                }}
              >
                <div
                  className="relative w-full"
                  style={{ height: `${measuredHeight.current - 16}px` }}
                >
                  <div className="absolute inset-0 rounded-2xl bg-white/60 backdrop-blur-xl" />
                  <div className="relative z-10 flex h-full w-full items-center px-6 py-1">
                    <div className="mr-6 h-24 w-24 flex-shrink-0 overflow-hidden rounded-full">
                      <Skeleton className="h-full w-full" />
                    </div>
                    <div className="flex h-full min-w-0 flex-1 flex-col justify-center">
                      <Skeleton className="mb-2 h-5 w-2/3" />
                      <Skeleton className="mb-1 h-4 w-full" />
                      <Skeleton className="mb-3 h-4 w-5/6" />
                      <div className="flex items-center justify-between pb-2 pt-1">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-6 w-6 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.li>
            ))}
          </ul>
        ) : (
          <>
            {!hasActiveFilters && filteredAndSortedItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-lg text-gray-600">
                  {emptyStateMessage || `No ${itemTypePlural} yet`}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  {emptyStateSubMessage || `${itemTypePlural} you create will appear here`}
                </p>
              </div>
            )}

            {hasActiveFilters && filteredAndSortedItems.length === 0 && (
              <>
                {filterState.bookmarkedOnly ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    {filterVariant === 'favorite' ? (
                      <Heart className="mx-auto mb-4 h-12 w-12 text-gray-300" />
                    ) : (
                      <Bookmark className="mx-auto mb-4 h-12 w-12 text-gray-300" />
                    )}
                    <p className="text-lg text-gray-600">
                      No {filterVariant === 'favorite' ? 'favorited' : 'bookmarked'}{' '}
                      {itemTypePlural} found
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                      {filterState.search !== '' || filterState.tagIds.length > 0
                        ? 'Try adjusting your filters'
                        : `${filterVariant === 'favorite' ? 'Favorite' : 'Bookmark'} ${itemTypePlural} to see them here`}
                    </p>
                  </div>
                ) : (
                  <NoResultsFound message={`No ${itemTypePlural} match your filters`} />
                )}
              </>
            )}

            {filteredAndSortedItems.length > 0 && (
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const startIndex = virtualRow.index * columnCount
                  const rowItems = filteredAndSortedItems.slice(
                    startIndex,
                    startIndex + columnCount
                  )

                  return (
                    <div
                      key={virtualRow.key}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                        paddingBottom: '16px',
                      }}
                    >
                      <div
                        className="grid gap-4"
                        style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
                      >
                        {rowItems.map((item, index) => {
                          const globalIndex = startIndex + index
                          const isInitialLoad = !hasAnimatedRef.current && globalIndex < 20

                          let staggerDelay = 0
                          if (isInitialLoad) {
                            staggerDelay = globalIndex * 0.03
                          } else if (hasAnimatedRef.current) {
                            const effectiveIndex =
                              scrollDirectionRef.current === 'up'
                                ? rowItems.length - 1 - index
                                : index
                            staggerDelay = effectiveIndex * 0.05
                          }

                          return (
                            <motion.div
                              key={item.id}
                              ref={globalIndex === 0 ? measureRef : undefined}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.3,
                                delay: staggerDelay,
                                ease: 'easeOut',
                              }}
                            >
                              {renderItem(item, globalIndex)}
                            </motion.div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {isFetching && filteredAndSortedItems.length > 0 && (
              <div className="flex justify-center py-4">
                <Spinner variant="default" size={24} />
                <span className="ml-2 text-sm text-gray-600">Loading more {itemTypePlural}...</span>
              </div>
            )}

            {pagination && filteredAndSortedItems.length > 0 && (
              <div className="text-center text-sm text-gray-500">
                {hasActiveFilters ? (
                  <>
                    Showing {filteredAndSortedItems.length} matching {itemTypePlural}
                    <span className="block text-xs">
                      (filtered from {items.length} loaded
                      {pagination.hasMore && ', scroll for more'})
                    </span>
                  </>
                ) : (
                  <>
                    Showing {filteredAndSortedItems.length} of {pagination.total} {itemTypePlural}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
